import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { MademindLayout } from "./MademindHome";
import { TenantsRoute } from "./TenantsRoute";
import { OperatorsTab } from "./OperatorsTab";
import { AuditTab } from "./AuditTab";
import { SystemTemplatesTab } from "./SystemTemplatesTab";
import { EmailsTab } from "./EmailsTab";

/**
 * Router of the Mademind console.
 *
 * Until now the console tracked its section in `useState`: the address bar
 * never changed, so you could not link a colleague to a screen, the back
 * button left the console entirely, and a reload always dropped you back on
 * Tenants. Every section is now a real path, and the screens that filter
 * (email search, audit) keep their filters in the query string — which is
 * what makes "look at this bounce" a link you can paste.
 *
 * It is a SEPARATE router from the tenant app's (`../router.tsx`): the console
 * has no module manifests, no tenant settings, and a different navigation
 * shape. The tenant app declares the global `Register` type for
 * `@tanstack/react-router`, so navigation here goes through untyped paths.
 */
export const MADEMIND_SECTIONS = ["tenants", "operators", "audit", "templates", "emails"] as const;

export type MademindSection = (typeof MADEMIND_SECTIONS)[number];

/** Filters that survive in the URL, so a search can be shared or bookmarked. */
export interface EmailSearchParams {
  readonly to?: string;
  readonly tenantId?: string;
  readonly status?: string;
  readonly kind?: string;
  readonly reason?: string;
}

export interface AuditSearchParams {
  readonly action?: string;
}

function readString(v: unknown): string | undefined {
  return typeof v === "string" && v !== "" ? v : undefined;
}

export function buildMademindRouter() {
  const rootRoute = createRootRoute({ component: () => <Outlet /> });

  const consoleRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/$tenantSlug",
    component: MademindLayout,
  });

  // Landing on the console root goes to Tenants, replacing history so the
  // back button does not bounce between the redirect and its target.
  const indexRoute = createRoute({
    getParentRoute: () => consoleRoute,
    path: "/",
    component: function ConsoleIndexRedirect() {
      const navigate = useNavigate();
      const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
      void navigate({ to: `/${tenantSlug}/tenants` as never, replace: true });
      return <p className="p-6 text-caption text-fg-muted">Reindirizzamento…</p>;
    },
  });

  const tenantsRoute = createRoute({
    getParentRoute: () => consoleRoute,
    path: "/tenants",
    component: TenantsRoute,
  });

  const operatorsRoute = createRoute({
    getParentRoute: () => consoleRoute,
    path: "/operators",
    component: OperatorsTab,
  });

  const auditRoute = createRoute({
    getParentRoute: () => consoleRoute,
    path: "/audit",
    component: AuditTab,
    validateSearch: (search: Record<string, unknown>): AuditSearchParams => {
      const action = readString(search["action"]);
      return action === undefined ? {} : { action };
    },
  });

  const templatesRoute = createRoute({
    getParentRoute: () => consoleRoute,
    path: "/templates",
    component: SystemTemplatesTab,
  });

  const emailsRoute = createRoute({
    getParentRoute: () => consoleRoute,
    path: "/emails",
    component: EmailsTab,
    validateSearch: (search: Record<string, unknown>): EmailSearchParams => ({
      ...(readString(search["to"]) !== undefined ? { to: readString(search["to"])! } : {}),
      ...(readString(search["tenantId"]) !== undefined
        ? { tenantId: readString(search["tenantId"])! }
        : {}),
      ...(readString(search["status"]) !== undefined
        ? { status: readString(search["status"])! }
        : {}),
      ...(readString(search["kind"]) !== undefined ? { kind: readString(search["kind"])! } : {}),
      ...(readString(search["reason"]) !== undefined
        ? { reason: readString(search["reason"])! }
        : {}),
    }),
  });

  // Anything else under the console: say so instead of rendering a blank
  // frame, and offer the way back.
  const notFoundRoute = createRoute({
    getParentRoute: () => consoleRoute,
    path: "/$",
    component: function ConsoleNotFound() {
      const navigate = useNavigate();
      const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
      return (
        <div>
          {/* Compact density: the title is a label, not a spoken sentence. */}
          <h1 className="text-h3 font-semibold mb-2">Sezione inesistente</h1>
          <p className="text-sm text-fg-muted mb-4">
            L&apos;indirizzo non corrisponde a nessuna sezione della console.
          </p>
          <button
            type="button"
            className="text-sm underline"
            onClick={() => void navigate({ to: `/${tenantSlug}/tenants` as never })}
          >
            Torna ai tenant
          </button>
        </div>
      );
    },
  });

  const routeTree = rootRoute.addChildren([
    consoleRoute.addChildren([
      indexRoute,
      tenantsRoute,
      operatorsRoute,
      auditRoute,
      templatesRoute,
      emailsRoute,
      notFoundRoute,
    ]),
  ]);

  return createRouter({ routeTree, defaultViewTransition: true });
}
